import { useEffect, useMemo, useState } from 'react';
import {
  Entity,
  RELATION_PROVIDES_API,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  CatalogApi,
  catalogApiRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import {
  GATEWAY_DOMAIN_ANNOTATION,
  GATEWAY_ROUTES_ANNOTATION,
  GatewayEnv,
  parseGatewayDomains,
  parseGatewayRoutes,
} from '../shared/gateway';
import { sharedLoad } from './cache';
import { isApi, isComponent } from './entityKinds';

/**
 * Gateway data of a component (AC-1). `byx-catalog` writes the gateway
 * annotations on the API `<app>-gateway`, which the component lists in
 * `providesApis`; the component itself has none. The provided APIs are
 * loaded in one batched call, with their annotations only.
 */

/** Route path of the Gateway tab, also the target of "Ver todas". */
export const GATEWAY_TAB_PATH = '/gateway';

export type ProvidedGateway = {
  envs: GatewayEnv[];
  domains: string[];
};

export type ProvidedGatewayState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; value: ProvidedGateway };

/** Refs of the APIs the entity provides. */
export function providedApiRefs(entity: Entity): string[] {
  return getEntityRelations(entity, RELATION_PROVIDES_API).map(ref =>
    stringifyEntityRef(ref),
  );
}

/** True when the entity provides at least one API. */
export function providesApis(entity: Entity): boolean {
  return providedApiRefs(entity).length > 0;
}

export async function loadProvidedGateway(
  catalogApi: CatalogApi,
  refs: string[],
): Promise<ProvidedGateway> {
  if (refs.length === 0) {
    return { envs: [], domains: [] };
  }
  const { items } = await catalogApi.getEntitiesByRefs({
    entityRefs: refs,
    fields: [
      'kind',
      'metadata.name',
      'metadata.namespace',
      'metadata.annotations',
    ],
  });
  const envs: GatewayEnv[] = [];
  const domains: string[] = [];
  for (const api of items) {
    const annotations = api?.metadata.annotations ?? {};
    envs.push(...parseGatewayRoutes(annotations[GATEWAY_ROUTES_ANNOTATION]));
    domains.push(
      ...parseGatewayDomains(annotations[GATEWAY_DOMAIN_ANNOTATION]),
    );
  }
  return { envs, domains: [...new Set(domains)] };
}

/** The gateway routes and domains of the APIs the entity provides. */
export function useProvidedGateway(entity: Entity): ProvidedGatewayState {
  const catalogApi = useApi(catalogApiRef);
  const refs = useMemo(
    () => providedApiRefs(entity).sort((a, b) => a.localeCompare(b)),
    [entity],
  );
  const key = refs.join(',');
  const [state, setState] = useState<ProvidedGatewayState>({
    status: 'loading',
  });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    sharedLoad(`gateway|${key}`, () =>
      loadProvidedGateway(catalogApi, refs),
    ).then(
      value => active && setState({ status: 'ready', value }),
      () => active && setState({ status: 'error' }),
    );
    return () => {
      active = false;
    };
    // `refs` is derived from `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, key]);

  return state;
}

/** The gateway declaration of an API, from its own annotations (SR-03). */
export function ownGateway(entity: Entity): ProvidedGateway {
  const annotations = entity.metadata.annotations ?? {};
  return {
    envs: parseGatewayRoutes(annotations[GATEWAY_ROUTES_ANNOTATION]),
    domains: parseGatewayDomains(annotations[GATEWAY_DOMAIN_ANNOTATION]),
  };
}

/** True when the API's own annotation declares at least one route. */
export function hasOwnGatewayRoutes(entity: Entity): boolean {
  return ownGateway(entity).envs.some(env => env.routes.length > 0);
}

/**
 * Where the Gateway tab may show: a component that provides an API (the
 * runtime guard below then hides it when those APIs have no route), or an
 * API whose own annotation has routes.
 */
export function mayHaveGatewayTab(entity: Entity): boolean {
  if (isApi(entity)) {
    return hasOwnGatewayRoutes(entity);
  }
  return isComponent(entity) && providesApis(entity);
}

/** The gateway data of the page's entity: its own (API) or provided. */
export function useEntityGateway(entity: Entity): ProvidedGatewayState {
  const provided = useProvidedGateway(entity);
  if (isApi(entity)) {
    return { status: 'ready', value: ownGateway(entity) };
  }
  return provided;
}

/**
 * Runtime no-data guard of the tab (AC-7): `yes` once routes are known;
 * `no` when the load ends with none or fails (the tab is hidden, and a deep
 * link goes to Visão geral — D-13, A3-1); `unknown` only while loading, so a
 * deep link is not bounced before the data arrives.
 */
export function useGatewayTabAvailability(
  entity: Entity,
): 'yes' | 'no' | 'unknown' {
  const state = useEntityGateway(entity);
  if (state.status === 'loading') {
    return 'unknown';
  }
  if (state.status === 'error') {
    return 'no';
  }
  return state.value.envs.some(env => env.routes.length > 0) ? 'yes' : 'no';
}
